/* eslint-disable @typescript-eslint/no-unused-vars */
import { posthog } from 'posthog-js';
import { getCookie } from './cookies-handlers';
import {
  getUserEmail,
  getUserFullName,
  getUserFirstName,
  getUserLastName,
  getUserShAccountId,
  getUserId,
  getUserPlanName,
  getUserPlanCode,
  getUserLeadFinderPlanName,
  getUserLeadFinderPlanCode,
  getUserInboxAnalyzerPlanName,
  getUserInboxAnalyzerPlanCode,
} from './user-details';
import store from '../../store';
import { handlePaddleEvent } from './paddle-event-dispatcher';
// eslint-disable-next-line import/no-cycle
import { getPostLoadMeta } from '../../components/home/helpers/meta-api';

// User identification flag to prevent anonymous events
let isUserIdentified = false;
// Tracks whether PostHog itself has been identified with a real email.
// The raw `trackPosthogEvent` fetch creates/updates a person profile, so it
// must only run once we have a real identity — otherwise it mints email-less
// "junk" persons. Set in identifyPosthogUser, cleared in resetUserIdentification.
let isPosthogIdentified = false;
let eventQueue: Array<{ method: string; args: any[] }> = [];

/**
 * Set user identification status
 * @param status - Whether user is identified
 */
export const setUserIdentified = (status: boolean): void => {
  isUserIdentified = status;
};

/**
 * Get user identification status
 * @returns Whether user is identified
 */
export const getUserIdentified = (): boolean => isUserIdentified;

/**
 * Ensure a PostHog host has an explicit scheme. A reverse-proxy host configured
 * without `https://` (e.g. `s.saleshandy.com`) is treated by the browser as a
 * relative path and gets appended to the app origin, producing broken URLs like
 * `https://my.saleshandy.com/s.saleshandy.com/e/` (404/405). Prefixing `https://`
 * keeps it an absolute host.
 */
const normalizePosthogHost = (host?: string): string | undefined =>
  host && !/^https?:\/\//i.test(host) ? `https://${host}` : host;

/**
 * Capture a PostHog event via the SDK (posthog.capture).
 */
export const capturePosthogEvent = (
  eventName: string,
  parameters?: Record<string, any>,
): void => {
  try {
    posthog.capture(eventName, parameters);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to capture PostHog event:', error);
  }
};

// First-touch attribution. Under PostHog's default `person_profiles:
// 'identified_only'`, anonymous events never write person properties, so
// $initial_* is only stamped when we identify. We persist the first-touch
// campaign/referrer on landing and replay it as $set_once — which restores
// attribution WITHOUT re-creating anonymous/email-less persons.
const FIRST_TOUCH_COOKIE = 'sh_ph_first_touch';
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

/**
 * Persist the first-touch UTMs / referrer / landing page once (cross-subdomain,
 * 180 days) so they survive until the user signs up. Set-once: never overwrite
 * an existing capture, and only write when there is real acquisition signal.
 */
const captureFirstTouchAttribution = (): void => {
  if (typeof window === 'undefined' || getCookie(FIRST_TOUCH_COOKIE)) return;

  const params = new URLSearchParams(window.location.search);
  const utms = UTM_KEYS.reduce<Record<string, string>>((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});

  const referrer = document.referrer || '';
  // Nothing to attribute (direct/internal navigation) — don't write a cookie.
  if (!Object.keys(utms).length && !referrer) return;

  const payload = encodeURIComponent(
    JSON.stringify({ utms, referrer, landingPage: window.location.href }),
  );
  const maxAge = 60 * 60 * 24 * 180; // 180 days
  const domain = window.location.hostname.endsWith('saleshandy.com')
    ? '; domain=.saleshandy.com'
    : '';
  document.cookie = `${FIRST_TOUCH_COOKIE}=${payload}; path=/; max-age=${maxAge}${domain}; SameSite=Lax`;
};

/**
 * Build the $initial_* $set_once payload from the persisted first-touch cookie,
 * falling back to the cross-domain landing/referrer cookies (sh_ulp / shreferer)
 * for marketing-first journeys the app never saw directly. Returns only the keys
 * we actually have, so $set_once never clobbers anything.
 */
const getFirstTouchSetOnce = (): Record<string, string> => {
  const setOnce: Record<string, string> = {};

  try {
    const raw = getCookie(FIRST_TOUCH_COOKIE);
    if (raw) {
      const parsed = JSON.parse(decodeURIComponent(raw));
      Object.entries(parsed?.utms || {}).forEach(([key, value]) => {
        if (value) setOnce[`$initial_${key}`] = String(value);
      });
      if (parsed?.referrer) setOnce.$initial_referrer = String(parsed.referrer);
      if (parsed?.landingPage) {
        setOnce.$initial_current_url = String(parsed.landingPage);
      }
    }
  } catch (error) {
    // Malformed cookie — fall through to the cookie fallbacks below.
  }

  // Fallback: derive UTMs from the landing-page cookie if we still have none.
  if (!setOnce.$initial_utm_medium) {
    try {
      const landing = getCookie('sh_ulp');
      if (landing) {
        const url = new URL(decodeURIComponent(landing));
        UTM_KEYS.forEach((key) => {
          const value = url.searchParams.get(key);
          if (value && !setOnce[`$initial_${key}`]) {
            setOnce[`$initial_${key}`] = value;
          }
        });
        if (!setOnce.$initial_current_url) {
          setOnce.$initial_current_url = url.toString();
        }
      }
    } catch (error) {
      // sh_ulp is not a full URL — nothing to parse.
    }
  }
  if (!setOnce.$initial_referrer) {
    const ref = getCookie('shreferer');
    if (ref) setOnce.$initial_referrer = decodeURIComponent(ref);
  }

  return setOnce;
};

/**
 * Track event to Posthog via direct fetch.
 * posthog-js v1.357 silently drops external posthog.capture() calls due to an
 * internal transport issue, while its own plugin events (heatmap, web vitals)
 * use a separate transport and work fine. Sending directly to the capture API
 * bypasses this and is confirmed to work.
 */
export const trackPosthogEvent = (
  eventName: string,
  parameters?: Record<string, any>,
) => {
  // Only mirror to PostHog once we have a real, email-bearing identity. This
  // raw fetch processes a person profile, so firing it for an unidentified
  // session would create an email-less person.
  if (!isPosthogIdentified) {
    return;
  }
  const posthogToken = (posthog as any).config?.token;
  const posthogApiHost =
    (posthog as any).config?.api_host ||
    normalizePosthogHost(process.env.REACT_APP_POSTHOG_API_HOST) ||
    'https://us.i.posthog.com';
  const posthogDistinctId = posthog.get_distinct_id?.();
  if (posthogToken && posthogDistinctId) {
    const properties: Record<string, any> = {
      ...parameters,
      $current_url: window.location.href,
    };
    // Stamp first-touch attribution ($initial_*) onto the event itself via
    // $set_once. Under person-on-events the person snapshot is frozen on each
    // event at ingest time. Replaying first-touch only at identify (#7627) is
    // too late for a conversion event captured in the same tick — the snapshot
    // freezes empty. Setting $set_once on THIS event makes PostHog apply the
    // person props while ingesting it, so "Sign-up Completed" carries
    // $initial_utm_* in its own snapshot. This restores what #7558 removed (the
    // early anonymous identify that stamped first-touch before sign-up) without
    // re-creating email-less persons. $set_once never overwrites, so it's safe
    // to send on every event; a caller-supplied $set_once still wins.
    const firstTouch = getFirstTouchSetOnce();
    if (Object.keys(firstTouch).length) {
      properties.$set_once = { ...firstTouch, ...(parameters?.$set_once || {}) };
    }
    fetch(`${posthogApiHost}/capture/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        api_key: posthogToken,
        event: eventName,
        distinct_id: posthogDistinctId,
        properties,
        timestamp: new Date().toISOString(),
      }),
    }).catch((err) => {
      console.error('[PostHog] Failed to send event:', err);
    });
  }
};

/**
 * Normalize analytics.track() args to { eventName, parameters }
 * Handles both call signatures:
 *   - Standard:    track(eventName, properties)
 *   - Object form: track({ event, properties, userId })
 */
const normalizeTrackArgs = (
  args: any[],
): { eventName: string; parameters?: Record<string, any> } => {
  if (args.length === 1 && typeof args[0] === 'object' && args[0]?.event) {
    return { eventName: args[0].event, parameters: args[0].properties };
  }
  return { eventName: args[0], parameters: args[1] };
};

/**
 * Identify user in Posthog
 */
const identifyPosthogUser = (
  trackingId: string,
  userData: Record<string, any>,
) => {
  // Never identify without a real email. An empty-email identify upgrades the
  // anonymous person to a "full" profile with no email — the single biggest
  // source of junk persons on the web side. Defer until the email is known
  // (callers re-run identification once the user profile has loaded).
  const email = userData?.email || userData?.$email;
  if (!trackingId || !email) {
    return;
  }
  // Pin PostHog's special $email/$name props so the Person shows the email/name
  // in Activity instead of the raw distinct id.
  const displayName = userData?.name || userData?.$name;
  // Third arg is $set_once: replay first-touch attribution so $initial_* lands
  // on the (real, email-bearing) person without an anonymous identify.
  posthog.identify(
    trackingId,
    {
      ...userData,
      email,
      $email: email,
      ...(displayName ? { name: displayName, $name: displayName } : {}),
    },
    getFirstTouchSetOnce(),
  );
  isPosthogIdentified = true;
};

/**
 * Flush queued events after user identification
 */
export const flushEventQueue = (): void => {
  eventQueue.forEach(({ method, args }) => {
    // Send to Segment (the overridden track will also forward to PostHog)
    if (
      typeof (window as any)?.analytics === 'object' &&
      (window as any)?.analytics?.[method]
    ) {
      (window as any)?.analytics?.[method]?.(...args);
    } else if (method === 'track') {
      // Segment not available — send directly to PostHog
      const { eventName, parameters } = normalizeTrackArgs(args);
      trackPosthogEvent(eventName, parameters);
    }
  });

  eventQueue = [];
};

/**
 * Queue event for later execution when user is identified
 * @param method - Analytics method name
 * @param args - Method arguments
 */
export const queueEvent = (method: string, ...args: any[]): void => {
  eventQueue.push({ method, args });
};

/**
 * Reset user identification when user logs out
 * This prevents events from being sent after logout
 */
export const resetUserIdentification = (): void => {
  setUserIdentified(false);
  isPosthogIdentified = false;
  eventQueue = []; // Clear queued events on logout

  // Reset Segment analytics
  if (
    typeof (window as any)?.analytics === 'object' &&
    (window as any)?.analytics?.reset
  ) {
    (window as any)?.analytics?.reset?.();
  }

  // Reset Posthog
  posthog.reset();
};

/**
 * API / Redux may send null for a missing name part. Trim and coerce
 * null/undefined to '' so template strings don't produce "John null".
 */
const normalizePersonNamePart = (value?: string | null): string => {
  if (value == null || value === '') return '';
  return String(value).trim();
};

/** First + last name for analytics; omits missing parts (e.g. no last name). */
export const buildPersonDisplayName = (
  firstName?: string | null,
  lastName?: string | null,
): string | undefined => {
  const parts = [
    normalizePersonNamePart(firstName),
    normalizePersonNamePart(lastName),
  ].filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
};

type PosthogBootstrapIds = {
  distinctID: string;
  sessionID: string;
};

/**
 * Read PostHog distinct_id / session_id forwarded via the URL (query or hash).
 * Lets a person/session continue across domains (e.g. marketing site -> app)
 * instead of a fresh anonymous person being created on the new origin.
 */
const readPosthogBootstrapIdsFromUrl = (): PosthogBootstrapIds | undefined => {
  if (typeof window === 'undefined') return undefined;

  const fromSearch = new URLSearchParams(window.location.search);
  const fromHash = new URLSearchParams(window.location.hash.slice(1));

  const distinctId =
    fromSearch.get('distinct_id') || fromHash.get('distinct_id');
  const sessionId = fromSearch.get('session_id') || fromHash.get('session_id');

  if (!distinctId || !sessionId) return undefined;

  return { distinctID: distinctId, sessionID: sessionId };
};

/**
 * Append the current PostHog distinct_id / session_id to a URL's hash so an
 * outbound cross-domain link can hand identity off to the destination origin
 * (pairs with the bootstrap read on the receiving side).
 */
export const attachPosthogIdsToUrl = (url: string): string => {
  if (!url || typeof window === 'undefined') return url;

  const distinctId = posthog.get_distinct_id?.();
  const sessionId = posthog.get_session_id?.();
  if (!distinctId || !sessionId) return url;

  try {
    const nextUrl = new URL(url, window.location.origin);
    const hashParams = new URLSearchParams(nextUrl.hash.replace(/^#/, ''));
    hashParams.set('distinct_id', String(distinctId));
    hashParams.set('session_id', String(sessionId));
    nextUrl.hash = hashParams.toString();
    return nextUrl.toString();
  } catch (error) {
    return url;
  }
};

/**
 * Posthog Analytics initialization
 */
export const initializePosthog = (posthogToken: string) => {
  if (!posthogToken) {
    return;
  }

  posthog.init(posthogToken, {
    // Reverse-proxy ready: send to a first-party proxy domain when
    // REACT_APP_POSTHOG_API_HOST is set (falls back to PostHog direct).
    // ui_host keeps in-app links (toolbar, etc.) pointing at PostHog itself.
    api_host:
      normalizePosthogHost(process.env.REACT_APP_POSTHOG_API_HOST) ||
      'https://us.i.posthog.com',
    ui_host: 'https://us.posthog.com',
    // localStorage is per-origin; pairing it with a cookie lets identity
    // persist across saleshandy.com subdomains (marketing -> app, etc.)
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: true,
    autocapture: false,
    capture_exceptions: true,
    // Adopt a distinct_id/session_id handed off via the URL so the same Person
    // and session continue across domains instead of a new anonymous person.
    bootstrap: readPosthogBootstrapIdsFromUrl(),
  });

  // Record first-touch attribution on landing so it can be replayed as
  // $set_once at identify — anonymous events don't write person properties
  // under PostHog's default person_profiles: 'identified_only'.
  captureFirstTouchAttribution();
};

/**
 * Segment Analytics event tracking utility
 * Segment automatically sends events to Google Analytics
 * Only tracks events if user is identified to prevent anonymous events
 */
export const trackGAEvent = (
  eventName: string,
  parameters?: Record<string, any>,
) => {
  if (isUserIdentified) {
    if (
      typeof (window as any)?.analytics === 'object' &&
      (window as any)?.analytics?.track
    ) {
      // The overridden track will automatically mirror to PostHog
      (window as any)?.analytics?.track?.(eventName, parameters);
    } else {
      // Segment not available — send directly to PostHog
      trackPosthogEvent(eventName, parameters);
    }
  } else {
    // Queue the event for later if user is not identified
    queueEvent('track', eventName, parameters);
  }
};

/**
 * Safe analytics track function that respects user identification
 * This is a drop-in replacement for window.analytics?.track
 * @param eventData - Event data object with userId, event, and properties
 */
export const safeAnalyticsTrack = (eventData: {
  userId?: string;
  event: string;
  properties?: Record<string, any>;
}): void => {
  const { event, properties } = eventData;
  trackGAEvent(event, properties);
};

type InitializeThirdPartyIntegrations = {
  trackingId: string;
  showChatSupport?: boolean;
  firstName: string;
  lastName: string;
  email: string;
};

export const initializeThirdPartyIntegrations = ({
  trackingId,
  showChatSupport = false,
  firstName = '',
  lastName = '',
  email = '',
}: InitializeThirdPartyIntegrations): void => {
  const displayName = buildPersonDisplayName(firstName, lastName);

  if (trackingId) {
    // Set user identification flag to prevent anonymous events
    setUserIdentified(true);

    // Freshworks CRM Initialization
    window.fwcrm &&
      window?.fwcrm?.identify(trackingId, {
        'First Name': firstName,
        'Last Name': lastName,
        Email: email,
      });

    // Google Analytics Initialization
    window.analytics &&
      window?.analytics?.identify(trackingId, {
        'First Name': firstName,
        'Last Name': lastName,
        email,
        'Referral page': getCookie('shreferer'),
        'Landing page': getCookie('sh_ulp'),
        landingPage: getCookie('sh_ulp'),
        referralPage: getCookie('shreferer'),
      });

    // Ensure PostHog is initialized before identifying (fixes ordering bug:
    // componentDidUpdate runs before useEffect, so initializePosthog from
    // useGAIntegrationWithConfig may not have run yet)
    const posthogToken = process.env.REACT_APP_CFG_EVT_02;
    // eslint-disable-next-line no-underscore-dangle
    if (posthogToken && !(posthog as any).__loaded) {
      initializePosthog(posthogToken);
    }

    // Posthog User Identification — set PostHog special props ($email/$name) and
    // null-safe name parts so the Person shows email/name, not the distinct id.
    const referrer = getCookie('shreferer');
    identifyPosthogUser(trackingId, {
      email,
      $email: email,
      ...(displayName ? { name: displayName, $name: displayName } : {}),
      ...(firstName ? { 'First Name': firstName } : {}),
      ...(lastName ? { 'Last Name': lastName } : {}),
      ...(referrer ? { $referrer: referrer } : {}),
    });

    // Flush any queued events that were waiting for user identification
    flushEventQueue();
  }
};

async function updateCelloAuthToken() {
  const reponse = await getPostLoadMeta();
  return reponse?.payload?.cellAuthToken;
}

export const initializeCello = () => {
  const state = store.getState().home;
  const celloAuthToken = state.getPostLoadMetaResponse?.celloAuthToken;

  // Prevent duplicate script injection
  if (document.getElementById('cello-script') || window.cello?.initialized) {
    return;
  }

  window.cello = window.cello || { cmd: [], initialized: true };

  // First step: Initialize Cello library
  const celloScript = document.createElement('script');
  celloScript.id = 'cello-script';
  celloScript.type = 'module';
  celloScript.src = process.env.REACT_APP_CELLO_SCRIPT;
  celloScript.async = true;

  if (celloAuthToken) {
    setTimeout(() => {
      document.body.appendChild(celloScript);
      window.cello.cmd.push((cello) => {
        cello.boot({
          productId: process.env.REACT_APP_CELLO_PRODUCT_ID,
          token: celloAuthToken,
          showOnBoot: true,
          customLauncherSelector: '.celloClass',
          hideDefaultLauncher: true,
          language: 'en',
          productUserDetails: {
            firstName: getUserFirstName(),
            lastName: getUserLastName(),
            fullName: getUserFullName(),
            email: getUserEmail(),
          },
          onTokenExpiring: async () => {
            const newToken = await updateCelloAuthToken();
            await window.Cello('updateToken', newToken);
          },
          onTokenExpired: async () => {
            const newToken = updateCelloAuthToken();
            await window.Cello('updateToken', newToken);
          },
        });
      });
    }, 1000);
  }
};

export const initializeGTM = (gtmId: string, dataLayerName = 'dataLayer') => {
  if (!gtmId) return;

  // Prevent duplicate script injection
  if (document.getElementById('gtm-script')) return;

  // Create dataLayer if not exists
  (window as any)[dataLayerName] = (window as any)[dataLayerName] || [];
  (window as any)[dataLayerName].push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const f = document.getElementsByTagName('script')[0];
  const gtmScript = document.createElement('script');
  gtmScript.id = 'gtm-script';
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${
    dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''
  }`;

  // Insert before first script tag
  f.parentNode?.insertBefore(gtmScript, f);
};

export const initializeChurnKey = (appId: string) => {
  if (!appId) return;

  // Prevent duplicate script injection
  if (document.getElementById('churnkey-script') || window.churnkey?.created) {
    return;
  }

  // Initialize ChurnKey global object
  window.churnkey = { created: true };

  const churnKeyScript = document.createElement('script');
  churnKeyScript.id = 'churnkey-script';
  churnKeyScript.src = `https://assets.churnkey.co/js/app.js?appId=${appId}`;
  churnKeyScript.async = true;
  churnKeyScript.defer = true;

  const firstScript = document.getElementsByTagName('script')[0];

  if (firstScript?.parentNode) {
    firstScript.parentNode?.insertBefore(churnKeyScript, firstScript);
  } else {
    const target = document.head || document.body;
    target?.appendChild(churnKeyScript);
  }
};

export const initializeBetterBugs = (apiKey: string) => {
  if (!apiKey) return;

  // If instance already exists, return early
  if ((window as any).betterBugsInstance) {
    return;
  }

  // Check if scripts are already loaded
  const scriptsAlreadyLoaded = (window as any).Betterbugs;

  const createBetterBugsInstance = () => {
    if ((window as any).Betterbugs) {
      const instance = new (window as any).Betterbugs({
        apiKey,
        styles: {
          theme: 'light',
          primaryColor: '#1d4ed8',
          primaryTextColor: '#FFFFFF',
        },
        position: {
          bottom: '50px',
          right: '30px',
        },
        mode: 'production',
        showActionButton: false,
        enableAnnotation: true,
        captureRewindOnScreenshot: true,
        recordType: 'recordVideo',
        maxRecordingMinutes: 5,
        mainHeading: 'Submit Feedback',
        startRecordingBtnText: 'Submit a Feedback',
        mainHeaderText: 'Ready to share your feedback?',
        subHeaderText: 'Record your screen or speak out your thoughts',
        emailFieldLabel: 'Your email',
        titleFieldLabel: 'Feedback title',
        descriptionFieldLabel: 'Describe your feedback',
        emailFieldPlaceholder: 'you@company.com',
        titleFieldPlaceholder: 'What is this feedback about?',
        descriptionFieldPlaceholder:
          'Tell us what worked, what didn’t, or what you’d love to see next',
        successMessageHeaderText: 'Thanks for the feedback! 🎉',
        successMessageSubHeaderText:
          'We’ve saved it and our team will review shortly.',
      });

      // Store instance globally so it can be accessed by other components
      (window as any).betterBugsInstance = instance;

      instance?.setEmail(getUserEmail());
      instance?.setMetadata({
        shAccountId: getUserShAccountId(),
        userId: getUserId(),
        emailAccount: getUserEmail(),
        currentPlanName: getUserPlanName(),
        currentPlanCode: getUserPlanCode(),
        currentLFPlanName: getUserLeadFinderPlanName(),
        currentLFPlanCode: getUserLeadFinderPlanCode(),
        currentIAPlanName: getUserInboxAnalyzerPlanName(),
        currentIAPlanCode: getUserInboxAnalyzerPlanCode(),
      });
    }
  };

  if (scriptsAlreadyLoaded) {
    // Scripts are already loaded, create instance immediately
    createBetterBugsInstance();
  } else {
    // Load BetterBugs script dynamically (single script)
    const scriptSrc = 'https://cdn.betterbugs.io/scripts/latest/index.js';

    new Promise<void>((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(
        `script[src="${scriptSrc}"]`,
      );
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error(`Failed to load script: ${scriptSrc}`));
      document.head.appendChild(script);
    })
      .then(() => {
        // Initialize BetterBugs after script is loaded
        createBetterBugsInstance();
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('Error loading BetterBugs script:', error);
        }
      });
  }
};

/**
 * Segment Analytics initialization utility
 * Segment automatically sends data to Google Analytics and other platforms
 */
export const initializeGA = (segmentToken: string) => {
  if (!segmentToken) {
    // eslint-disable-next-line no-console
    console.warn('Segment token is not provided');
    return;
  }

  // Prevent duplicate script injection
  if (document.getElementById('segment-script')) {
    return;
  }

  // Initialize Segment analytics
  (window as any).analytics = (window as any).analytics || [];

  // Segment analytics methods
  const methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
  ];

  // Create analytics factory
  const factory = function (method: string) {
    return function (...args: any[]) {
      (window as any).analytics.push([method, ...args]);
    };
  };

  // Add methods to analytics
  methods.forEach((method) => {
    (window as any).analytics[method] = factory(method);
  });

  // Override the track method to use our user identification system
  const originalTrack = (window as any).analytics?.track;
  (window as any).analytics.track = (...args: any[]) => {
    const { eventName, parameters } = normalizeTrackArgs(args);
    if (isUserIdentified) {
      originalTrack?.apply((window as any).analytics, args);
      trackPosthogEvent(eventName, parameters);
    } else {
      queueEvent('track', ...args);
    }
  };

  // Load Segment script
  const script = document.createElement('script');
  script.id = 'segment-script';
  script.async = true;
  script.src = `https://cdn.segment.com/analytics.js/v1/${segmentToken}/analytics.min.js`;

  // Add load event listener
  script.onload = () => {
    // Override the track method after Segment loads to ensure our system is used
    if (
      typeof (window as any).analytics === 'object' &&
      (window as any).analytics?.track
    ) {
      const segmentTrack = (window as any).analytics?.track;
      (window as any).analytics.track = (...args: any[]) => {
        const { eventName, parameters } = normalizeTrackArgs(args);
        if (isUserIdentified) {
          segmentTrack?.apply((window as any).analytics, args);
          trackPosthogEvent(eventName, parameters);
        } else {
          queueEvent('track', ...args);
        }
      };
    }
  };

  script.onerror = () => {
    // eslint-disable-next-line no-console
    console.error('Failed to load Segment Analytics script');
  };

  // Insert before first script tag
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
};

/**
 * Segment Analytics page view tracking utility
 * Segment automatically sends page views to Google Analytics
 * Only tracks page views if user is identified to prevent anonymous events
 */
export const trackGAPageView = (pagePath?: string, pageTitle?: string) => {
  if (isUserIdentified) {
    // Send immediately if user is identified
    if (
      typeof (window as any).analytics === 'object' &&
      (window as any).analytics?.page
    ) {
      (window as any).analytics?.page?.(pagePath, pageTitle);
    }
  } else {
    // Queue the page view for later if user is not identified
    queueEvent('page', pagePath, pageTitle);
  }
};

/**
 * Segment Analytics user identification utility
 * Segment automatically sends user data to Google Analytics
 * Sets user identification flag and flushes queued events
 */
export const identifyGAUser = (
  userId: string,
  userProperties?: Record<string, any>,
) => {
  if (
    typeof (window as any).analytics === 'object' &&
    (window as any).analytics?.identify
  ) {
    (window as any).analytics?.identify?.(userId, userProperties);
  }

  // Identify user in PostHog
  identifyPosthogUser(userId, userProperties || {});

  // Set user identification flag and flush queued events
  setUserIdentified(true);
  flushEventQueue();
};

type HighAdvocacyUser = {
  userId: string;
  email: string;
  name: string;
  subscriptionPlanType?: string;
  leadFinderPlanType?: string;
};

type InitializeHighAdvocacyParams = {
  containerId: string;
  campaignId: string;
  user: HighAdvocacyUser;
  darkMode?: boolean;
  onError?: (error: any) => void;
};

export const initializeHighAdvocacy = ({
  containerId,
  campaignId,
  user,
  darkMode = false,
  onError,
}: InitializeHighAdvocacyParams): void => {
  if (!containerId || !campaignId) return;
  if (!user?.userId || !user?.email) return;
  const containerEl = document.getElementById(containerId);
  if (!containerEl) return;

  // Set config (as per vendor snippet) before loading the widget script
  window.HighAdvocacy = {
    containerId,
    campaignId,
    darkMode,
    user,
  } as any;

  const existingScript = document.getElementById('highadvocacy-widget');
  if (existingScript) {
    // SPA navigation case: script was loaded earlier but container is now mounted on this route.
    // If container is still empty, re-inject script to trigger widget initialization.
    if (containerEl.childElementCount === 0) {
      existingScript.parentNode?.removeChild(existingScript);
    } else {
      return;
    }
  }

  const script = document.createElement('script');
  script.id = 'highadvocacy-widget';
  script.src = process.env.REACT_APP_HIGH_ADVOCACY_URL;
  script.async = true;

  script.onerror = (event) => {
    const err = new Error('Failed to load HighAdvocacy widget script');
    (err as any).event = event;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(err);
    }
    onError?.(err);
  };

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    const target = document.head || document.body;
    target?.appendChild(script);
  }
};

/**
 * Paddle Script Injection and Initilization with initial config
 */
export const initializePaddle = (token: string, customerId?: string) => {
  if (!token) return;

  // Prevent duplicate script injection
  if (document.getElementById('paddle-script') || (window as any).Paddle) {
    return;
  }

  try {
    // Create script element
    const paddleScript = document.createElement('script');
    paddleScript.id = 'paddle-script';
    paddleScript.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    paddleScript.async = true;
    paddleScript.onload = () => {
      const isProduction = process.env.REACT_APP_ENV === 'production';
      if (isProduction) {
        (window as any).Paddle.Environment.set('production');
      } else {
        (window as any).Paddle.Environment.set('sandbox');
      }
      const initConfig: any = {
        token,
        pwCustomer: { id: customerId || '' },
        eventCallback: handlePaddleEvent,
      };

      (window as any).Paddle.Initialize(initConfig);
    };
    paddleScript.onerror = () => {
      console.error('[Paddle] Failed to load script from CDN');
    };

    const firstScript = document.getElementsByTagName('script')[0];
    const parentNode = firstScript?.parentNode;
    if (firstScript && parentNode && parentNode.contains(firstScript)) {
      parentNode.insertBefore(paddleScript, firstScript);
    } else {
      // Fallback: append to head or body
      const target = document.head || document.body;
      if (target) {
        target.appendChild(paddleScript);
      }
    }
  } catch (error) {
    console.error('[Paddle] Error during initialization', error);
  }
};
